import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router';
import type { AutopilotSettings, FaixaDeHorario } from '@zenda/api-contract';
import { Icon } from './Icon';
import { EditorDeFaixas } from './PilotoTab';
import { useT } from '../lib/i18n';
import { textoDoErro } from '../lib/aviso-de-erro';
import { usePilotoConfig, usePilotoResumo, useSalvarPilotoConfig } from '../lib/piloto';
import '../styles/piloto.css';

const reais = (n: number): string =>
  n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 });

/** O bloco "Piloto hoje" do painel (plano §6.4): o motivo mais comum é o que falta na memória. */
export function PilotoHoje(): ReactElement | null {
  const t = useT();
  const resumo = usePilotoResumo();
  if (resumo.data === undefined) return null;
  const r = resumo.data;
  const h = t.piloto.hoje;
  return (
    <section className="card piloto-secao piloto-hoje">
      <div className="piloto-secao__cabeca">
        <h3 className="piloto-secao__titulo">
          <Icon name="robot" size={14} /> {h.titulo}
        </h3>
        <Link to="/inbox?filtro=precisa" className="btn btn--ghost btn--sm">
          {t.piloto.precisam(r.needsYou + r.takenOver)}
        </Link>
      </div>
      {r.mode === 'off' ? (
        <p className="piloto-secao__texto">{h.desligado}</p>
      ) : (
        <>
          <div className="piloto-numeros">
            <div className="piloto-numero">
              <b className="num">{r.today.answered}</b>
              <span>{h.respondidas}</span>
            </div>
            <div className="piloto-numero">
              <b className="num">{r.today.escalated}</b>
              <span>{h.precisam}</span>
            </div>
            <div className="piloto-numero">
              <b className="num">{r.today.takenOver}</b>
              <span>{h.assumidas}</span>
            </div>
          </div>
          <p className="piloto-secao__texto">
            {r.today.topReason === null ? h.semMotivo : h.motivo(r.today.topReason)}
          </p>
          {r.budget === null || r.budget.usedBrl < r.budget.limitBrl * 0.8 ? null : (
            <p className="piloto-aviso piloto-aviso--pendente">
              <Icon name="alert" size={13} />
              {r.budget.usedBrl >= r.budget.limitBrl
                ? h.orcamentoEstourado
                : h.orcamento(
                    reais(r.budget.usedBrl),
                    reais(r.budget.limitBrl),
                    Math.round((r.budget.usedBrl / r.budget.limitBrl) * 100),
                  )}
            </p>
          )}
        </>
      )}
    </section>
  );
}

/** Horário de trabalho, prazo, aviso por e-mail, orçamento e som (plano §16, Configurações). */
export function SecaoPiloto(): ReactElement | null {
  const t = useT();
  const c = t.piloto.config;
  const config = usePilotoConfig();
  const salvar = useSalvarPilotoConfig();
  const [v, setV] = useState<Partial<AutopilotSettings>>({});
  const [salvo, setSalvo] = useState(false);
  useEffect(() => {
    if (config.data !== undefined) setV(config.data);
  }, [config.data]);
  if (config.data === undefined) return null;
  const mudar = (p: Partial<AutopilotSettings>) => {
    setSalvo(false);
    setV((x) => ({ ...x, ...p }));
  };
  const num = (s: string): number | null => (s.trim() === '' ? null : Number(s));
  const faixas: readonly FaixaDeHorario[] = v.workHours ?? [];

  return (
    <section className="card">
      <div className="card__head">
        <div>
          <p className="card__title">{c.titulo}</p>
          <p className="card__subtitle">{c.expedienteTexto}</p>
        </div>
      </div>
      <div className="piloto-secao">
        <p className="piloto-sub">{c.expediente}</p>
        <EditorDeFaixas
          faixas={faixas}
          onMudar={(f) => mudar({ workHours: f.length === 0 ? null : f })}
        />
        <div className="piloto-grade">
          <label>
            {c.prazo}
            <input
              className="piloto-campo"
              type="number"
              placeholder="15"
              value={v.alertMinutes ?? ''}
              onChange={(e) => mudar({ alertMinutes: num(e.target.value) })}
            />
          </label>
          <label>
            {c.emailDepois}
            <input
              className="piloto-campo"
              type="number"
              placeholder="20"
              value={v.emailAfterMinutes ?? ''}
              onChange={(e) => mudar({ emailAfterMinutes: num(e.target.value) })}
            />
          </label>
          <label>
            {c.orcamento}
            <input
              className="piloto-campo"
              type="number"
              min="0"
              value={v.monthlyBudgetBrl ?? ''}
              onChange={(e) => mudar({ monthlyBudgetBrl: num(e.target.value) })}
            />
          </label>
        </div>
        <p className="piloto-secao__texto">{c.orcamentoTexto}</p>
        <label className="piloto-rotulo">
          {c.email}
          <input
            className="piloto-campo"
            type="email"
            value={v.notifyEmail ?? ''}
            onChange={(e) => mudar({ notifyEmail: e.target.value })}
          />
          <span>{c.emailTexto}</span>
        </label>
        <label className="piloto-check">
          <input
            type="checkbox"
            checked={v.sound ?? true}
            onChange={(e) => mudar({ sound: e.target.checked })}
          />
          {c.som}
        </label>
        <label className="piloto-check">
          <input
            type="checkbox"
            checked={v.dailySummary ?? false}
            onChange={(e) => mudar({ dailySummary: e.target.checked })}
          />
          {c.resumo}
        </label>
        <div className="piloto-acoes">
          {salvar.isError ? (
            <span className="piloto-secao__texto">{textoDoErro(salvar.error)}</span>
          ) : null}
          {salvo ? <span className="piloto-ok">{c.salvo}</span> : null}
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={salvar.isPending}
            onClick={() =>
              salvar.mutate(
                {
                  workHours: v.workHours ?? null,
                  alertMinutes: v.alertMinutes ?? null,
                  emailAfterMinutes: v.emailAfterMinutes ?? null,
                  monthlyBudgetBrl: v.monthlyBudgetBrl ?? null,
                  notifyEmail: v.notifyEmail ?? null,
                  sound: v.sound ?? null,
                  dailySummary: v.dailySummary ?? null,
                },
                { onSuccess: () => setSalvo(true) },
              )
            }
          >
            {c.salvar}
          </button>
        </div>
      </div>
    </section>
  );
}
